import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  UserPlus,
  KeyRound,
  Trash2,
  ArrowLeft,
  ShieldCheck,
  Building2,
  Plus,
  Pencil,
  FileUp,
  IdCard,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { assetUrl, displayUserName } from "@/lib/assetUrl";

const roleLabel = (r) =>
  r === "admin" ? "Admin" : r === "super_user" ? "Super user" : "Korisnik";

const roleBadgeClass = (r) =>
  r === "admin"
    ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F5DA6A]"
    : r === "super_user"
      ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
      : "border-slate-500/40 bg-slate-500/10 text-slate-300";

export default function AdminPanel({ onBack }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(null);
  const [createCompanyOpen, setCreateCompanyOpen] = useState(false);
  const [renameCompany, setRenameCompany] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/api/users"),
        api.get("/api/companies").catch(() => []),
      ]);
      setUsers(u);
      setCompanies(c);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreateUser = async (form) => {
    try {
      await api.post("/api/users", form);
      toast.success("Nalog kreiran");
      setCreateUserOpen(false);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleChangePassword = async (id, password) => {
    try {
      await api.patch(`/api/users/${id}`, { password });
      toast.success("Lozinka promijenjena");
      setPwOpen(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEditUserInfo = async (id, form) => {
    try {
      await api.patch(`/api/users/${id}`, {
        first_name: form.first_name,
        last_name: form.last_name,
      });
      toast.success("Podaci sačuvani");
      setEditUserOpen(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await api.del(`/api/users/${id}`);
      toast.success("Nalog obrisan");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateCompany = async (form) => {
    try {
      const saved = await api.post("/api/companies", form);
      toast.success("Firma kreirana");
      setCreateCompanyOpen(false);
      await load();
      return saved;
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const handleRenameCompany = async (id, form) => {
    try {
      const saved = await api.patch(`/api/companies/${id}`, form);
      toast.success("Firma sačuvana");
      setRenameCompany(null);
      await load();
      return saved;
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await api.del(`/api/companies/${id}`);
      toast.success("Firma obrisana");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const superUsers = users.filter((u) => u.role === "super_user");

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-6 md:py-10 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-slate-300 hover:bg-[#1A1E2C]"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Nazad
          </Button>
          <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-white tracking-tight">
            Administracija
          </h2>
        </div>
      </div>

      {/* Companies section (admin only) */}
      {isAdmin && (
        <Card
          className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6"
          data-testid="admin-companies-card"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#D4AF37]" />
              <h3 className="font-serif-display text-xl text-white tracking-tight">
                Firme
              </h3>
              <span className="text-xs text-slate-500 font-mono-terminal ml-2">
                {companies.length}
              </span>
            </div>
            <Button
              onClick={() => setCreateCompanyOpen(true)}
              size="sm"
              className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
              data-testid="admin-create-company-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nova firma
            </Button>
          </div>

          {companies.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-[#272A35] rounded-lg">
              Nema firmi. Kreirajte prvu firmu da počnete.
            </div>
          ) : (
            <ul className="space-y-2">
              {companies.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#272A35] bg-[#0C0E14]/60 hover:bg-[#1A1E2C] transition-colors"
                  data-testid={`admin-company-${c.name}`}
                >
                  <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-terminal text-slate-100">
                      {c.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {c.username && (
                        <span className="font-mono-terminal text-[#D4AF37]">
                          @{c.username}
                        </span>
                      )}
                      <span>
                        · {c.user_count ?? 0}{" "}
                        {c.user_count === 1 ? "korisnik" : "korisnika"}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    onClick={() => setRenameCompany(c)}
                    data-testid={`admin-company-rename-${c.name}`}
                    aria-label="Preimenuj"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`admin-company-delete-${c.name}`}
                        aria-label="Obriši"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#131620] border-[#272A35]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                          Obrisati firmu "{c.name}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Svi korisnici i putovanja te firme biće obrisani. Ova akcija se ne može poništiti.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[#1A1E2C] border-[#363A49] text-slate-100 hover:bg-[#272A35]">
                          Odustani
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteCompany(c.id)}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          Obriši
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Users section */}
      <Card
        className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6"
        data-testid="admin-users-card"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-serif-display text-xl text-white tracking-tight">
            Korisnici
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 font-mono-terminal">
              {users.length} {users.length === 1 ? "nalog" : "naloga"}
            </span>
            <Button
              onClick={() => setCreateUserOpen(true)}
              size="sm"
              className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
              data-testid="admin-create-btn"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Novi nalog
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-400 py-8 text-center">Učitavanje...</div>
        ) : (
          <ul className="space-y-2">
            {users.map((u) => {
              const isMe = u.id === me?.id;
              const canDelete = isAdmin && !isMe;
              const canEditPassword =
                isAdmin ||
                isMe ||
                (me?.role === "super_user" &&
                  u.role === "user" &&
                  u.company_id === me.company_id);
              return (
                <li
                  key={u.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#272A35] bg-[#0C0E14]/60 hover:bg-[#1A1E2C] transition-colors flex-wrap sm:flex-nowrap"
                  data-testid={`admin-user-${u.username}`}
                >
                  <ShieldCheck className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-terminal text-slate-100">
                        {displayUserName(u)}
                      </span>
                      {isMe && (
                        <Badge
                          variant="outline"
                          className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] h-5 px-1.5"
                        >
                          vi
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-[10px] h-5 px-1.5 ${roleBadgeClass(u.role)}`}
                      >
                        {roleLabel(u.role)}
                      </Badge>
                      {u.company_name && (
                        <Badge
                          variant="outline"
                          className="border-white/10 bg-white/5 text-slate-300 text-[10px] h-5 px-1.5 gap-1"
                        >
                          <Building2 className="h-2.5 w-2.5" />
                          {u.company_name}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono-terminal">
                      #{u.id.slice(0, 8)}
                    </div>
                  </div>

                  {canEditPassword && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-slate-100 hover:bg-[#272A35]"
                      onClick={() => setEditUserOpen(u)}
                      data-testid={`admin-edit-info-${u.username}`}
                    >
                      <IdCard className="h-3.5 w-3.5 mr-1" />
                      Ime
                    </Button>
                  )}

                  {canEditPassword && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-slate-100 hover:bg-[#272A35]"
                      onClick={() => setPwOpen(u)}
                      data-testid={`admin-pw-${u.username}`}
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1" />
                      Lozinka
                    </Button>
                  )}

                  {isAdmin && !isMe && (
                    <Select
                      value={u.company_id || "__none__"}
                      onValueChange={async (v) => {
                        try {
                          await api.patch(`/api/users/${u.id}`, {
                            company_id: v === "__none__" ? null : v,
                          });
                          toast.success("Firma promijenjena");
                          await load();
                        } catch (err) {
                          toast.error(err.message);
                        }
                      }}
                    >
                      <SelectTrigger
                        className="w-[130px] h-8 bg-[#0C0E14] border-[#272A35] text-slate-200 text-xs"
                        data-testid={`admin-user-company-${u.username}`}
                      >
                        <SelectValue placeholder="Firma" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#131620] border-[#272A35]">
                        {u.role !== "admin" ? (
                          companies.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="__none__">Bez firme</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  )}

                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`admin-delete-${u.username}`}
                          aria-label="Obriši"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#131620] border-[#272A35]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">
                            Obrisati nalog "{u.username}"?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Svi njegovi unosi biće obrisani.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-[#1A1E2C] border-[#363A49] text-slate-100 hover:bg-[#272A35]">
                            Odustani
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteUser(u.id)}
                            className="bg-red-500 hover:bg-red-600 text-white"
                          >
                            Obriši
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <CreateUserDialog
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
        onCreate={handleCreateUser}
        companies={companies}
        superUsers={superUsers}
        me={me}
      />

      <ChangePasswordDialog
        user={pwOpen}
        onOpenChange={(v) => !v && setPwOpen(null)}
        onSave={handleChangePassword}
      />

      <EditUserInfoDialog
        user={editUserOpen}
        onOpenChange={(v) => !v && setEditUserOpen(null)}
        onSave={handleEditUserInfo}
      />

      <CompanyDialog
        open={createCompanyOpen}
        onOpenChange={setCreateCompanyOpen}
        onSave={handleCreateCompany}
        title="Nova firma"
      />

      <CompanyDialog
        open={!!renameCompany}
        onOpenChange={(v) => !v && setRenameCompany(null)}
        onSave={(form) => handleRenameCompany(renameCompany.id, form)}
        initial={renameCompany}
        title="Izmijeni firmu"
      />
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreate, companies, superUsers, me }) {
  const isAdmin = me?.role === "admin";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [companyId, setCompanyId] = useState("");

  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setUsername("");
      setPassword("");
      setRole("user");
      setCompanyId(!isAdmin ? me?.company_id || "" : "");
    }
  }, [open, isAdmin, me]);

  const submit = () => {
    if (!username.trim() || password.length < 4) {
      toast.error("Popunite korisničko ime i lozinku (min. 4 znaka)");
      return;
    }
    if (isAdmin && role !== "admin" && !companyId) {
      toast.error("Izaberite firmu za super usera ili korisnika");
      return;
    }
    const payload = {
      username: username.trim(),
      password,
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      role,
      company_id: role === "admin" ? null : companyId || undefined,
    };
    onCreate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#131620] border-[#272A35] text-slate-100 max-w-md"
        data-testid="admin-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl tracking-tight">
            Novi nalog
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-1.5">
            Unesite ime, prezime, korisničko ime, lozinku, ulogu i firmu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                Ime
              </Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="bg-[#0C0E14] border-[#272A35] text-slate-100"
                data-testid="create-first-name"
                placeholder="Marko"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                Prezime
              </Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="bg-[#0C0E14] border-[#272A35] text-slate-100"
                data-testid="create-last-name"
                placeholder="Marković"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
              Korisničko ime (za prijavu)
            </Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#0C0E14] border-[#272A35] text-slate-100"
              data-testid="create-username"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
              Lozinka
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0C0E14] border-[#272A35] text-slate-100"
              data-testid="create-password"
            />
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                Uloga
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  className="bg-[#0C0E14] border-[#272A35]"
                  data-testid="create-role"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131620] border-[#272A35]">
                  <SelectItem value="user">Korisnik</SelectItem>
                  <SelectItem value="super_user">Super user</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {role !== "admin" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                Firma {isAdmin && <span className="text-red-400">*</span>}
              </Label>
              {isAdmin ? (
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger
                    className="bg-[#0C0E14] border-[#272A35]"
                    data-testid="create-company"
                  >
                    <SelectValue placeholder="Izaberi firmu" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131620] border-[#272A35]">
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                    {companies.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-slate-500">
                        Prvo kreirajte firmu.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className="rounded-md border border-[#272A35] bg-[#0C0E14] px-3 py-2 text-sm text-slate-300 font-mono-terminal"
                  data-testid="create-company-auto"
                >
                  {me?.company_name || "vaša firma"}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
          >
            Odustani
          </Button>
          <Button
            onClick={submit}
            className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
            data-testid="create-submit"
          >
            Kreiraj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ user, onOpenChange, onSave }) {
  const [pw, setPw] = useState("");
  useEffect(() => {
    if (user) setPw("");
  }, [user]);
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131620] border-[#272A35] text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl tracking-tight">
            Nova lozinka za "{user?.username}"
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-1.5">
            Unesite novu lozinku (min. 4 znaka).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
            Nova lozinka
          </Label>
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="bg-[#0C0E14] border-[#272A35] text-slate-100"
            data-testid="pw-input"
          />
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
          >
            Odustani
          </Button>
          <Button
            onClick={() => {
              if (pw.length < 4) {
                toast.error("Lozinka mora imati bar 4 znaka");
                return;
              }
              onSave(user.id, pw);
            }}
            className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
            data-testid="pw-save"
          >
            Sačuvaj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserInfoDialog({ user, onOpenChange, onSave }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  useEffect(() => {
    if (user) {
      setFirst(user.first_name || "");
      setLast(user.last_name || "");
    }
  }, [user]);
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131620] border-[#272A35] text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl tracking-tight">
            Ime i prezime — "{user?.username}"
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-1.5">
            Ime i prezime se prikazuju u aplikaciji i u PDF izvještaju.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Ime</Label>
            <Input
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              className="bg-[#0C0E14] border-[#272A35] text-slate-100"
              data-testid="edit-first-name"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Prezime</Label>
            <Input
              value={last}
              onChange={(e) => setLast(e.target.value)}
              className="bg-[#0C0E14] border-[#272A35] text-slate-100"
              data-testid="edit-last-name"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
          >
            Odustani
          </Button>
          <Button
            onClick={() => onSave(user.id, { first_name: first.trim(), last_name: last.trim() })}
            className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
            data-testid="edit-info-save"
          >
            Sačuvaj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyDialog({ open, onOpenChange, onSave, initial, title }) {
  const [form, setForm] = useState({ name: "", username: "", address: "", phone: "", website: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || "",
        username: initial?.username || "",
        address: initial?.address || "",
        phone: initial?.phone || "",
        website: initial?.website || "",
      });
      setLogoFile(null);
      setLogoPreview(initial?.logo_url || null);
    }
  }, [open, initial]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updUname = (e) =>
    setForm((f) => ({
      ...f,
      username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""),
    }));

  const pickFile = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (f.type !== "image/png" && !name.endsWith(".png")) {
      toast.error("Dozvoljene su samo PNG datoteke");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Datoteka je veća od 2MB");
      return;
    }
    setLogoFile(f);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const removeLogo = async () => {
    if (initial?.id && initial?.logo_url && !logoFile) {
      try {
        await api.del(`/api/companies/${initial.id}/logo`);
        toast.success("Logo uklonjen");
      } catch (err) {
        toast.error(err.message);
        return;
      }
    }
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Naziv firme je obavezan");
      return;
    }
    if (!form.username || form.username.length < 2) {
      toast.error("Korisničko ime firme je obavezno (min. 2 znaka, a–z, 0–9, - _ .)");
      return;
    }
    setUploading(true);
    try {
      // onSave returns the created/updated company (via handleCreateCompany/handleRenameCompany).
      const saved = await onSave(form);
      const companyId = saved?.id || initial?.id;
      if (logoFile && companyId) {
        const fd = new FormData();
        fd.append("file", logoFile, logoFile.name);
        await api.upload(`/api/companies/${companyId}/logo`, fd);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131620] border-[#272A35] text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl tracking-tight">{title}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-1.5">
            Naziv firme je obavezan. Ostala polja su opciona.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Naziv firme *</Label>
            <Input value={form.name} onChange={upd("name")} className="bg-[#0C0E14] border-[#272A35] text-slate-100" data-testid="company-name-input" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Korisničko ime firme (za prijavu) *</Label>
            <Input
              value={form.username}
              onChange={updUname}
              className="bg-[#0C0E14] border-[#272A35] text-slate-100 font-mono-terminal lowercase"
              data-testid="company-username-input"
              placeholder="npr. expresstrans"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <p className="text-[11px] text-slate-500">
              Koriste ga vaši zaposleni prilikom prijave. Dozvoljeno: a–z, 0–9, znakovi <span className="font-mono-terminal">- _ .</span>
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Adresa</Label>
            <Input value={form.address} onChange={upd("address")} className="bg-[#0C0E14] border-[#272A35] text-slate-100" data-testid="company-address-input" placeholder="Ulica, grad, država" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Telefon</Label>
              <Input value={form.phone} onChange={upd("phone")} className="bg-[#0C0E14] border-[#272A35] text-slate-100" data-testid="company-phone-input" placeholder="+387 ..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Web stranica</Label>
              <Input value={form.website} onChange={upd("website")} className="bg-[#0C0E14] border-[#272A35] text-slate-100" data-testid="company-website-input" placeholder="https://..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Logo (PNG, max 2MB)</Label>
            <div className="flex items-start gap-3">
              <div
                className="h-20 w-20 rounded-md border border-[#272A35] bg-[#0C0E14] flex items-center justify-center overflow-hidden shrink-0"
                data-testid="company-logo-preview"
              >
                {logoPreview ? (
                  <div className="h-full w-full bg-white/95 p-1 flex items-center justify-center">
                    <img
                      src={
                        logoPreview.startsWith("data:") || logoPreview.startsWith("http")
                          ? logoPreview
                          : assetUrl(logoPreview)
                      }
                      alt="Logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <Building2 className="h-6 w-6 text-slate-600" />
                )}
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={pickFile}
                  className="bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100 justify-start"
                  data-testid="company-logo-upload-btn"
                >
                  <FileUp className="h-4 w-4 mr-1.5 text-[#D4AF37]" />
                  {logoPreview ? "Zamijeni logo" : "Otpremi PNG"}
                </Button>
                {logoPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={removeLogo}
                    className="text-red-400 hover:bg-red-500/10 hover:text-red-300 justify-start"
                    data-testid="company-logo-remove-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Ukloni logo
                  </Button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,.png"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="company-logo-file-input"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100">Odustani</Button>
          <Button
            onClick={handleSubmit}
            disabled={uploading}
            className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
            data-testid="company-save"
          >
            {uploading ? "Snimanje..." : "Sačuvaj"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
